/* eslint-disable @typescript-eslint/no-explicit-any */
import { useStaticQuery, graphql } from 'gatsby';
import { useIntl, Link } from 'gatsby-plugin-intl';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import Sticky from 'react-stickynode';

import { filterHeaderLinksForRegionLocked } from '../utils/regionLockedContent';
import { useProvince } from '../state/ProvinceProvider';
import useKeyValuePairs from '../hooks/useKeyValuePairs';

import SectionContainer from './SectionContainer';
import MobileStickyNav from './MobileStickyNav';
import NotificationBar from './NotificationBar';
import GlobalSearch from './SiteSearch/GlobalSearch';
import DropdownNavigation from './DropdownNavigation';
import BladeController from './BladeController';
import CallToAction from './CallToAction';
import LangAndProvinceSelector from './LangAndProvinceSelector';
import { HeaderButtonStyles, HeaderCallToAction } from './styles';
import ModalTrigger from './ModalTrigger';
import GetAQuoteButtonOrLink from './GetAQuoteRenewalCTA';

const StyledHeader = styled.header`
  background-color: ${themeGet('colors.white')};
  width: 100%;
  min-height: 70px;
`;

const StyledHeaderWrapper = styled.div`
  background-color: ${themeGet('colors.white')};
  border-bottom: 4px solid ${themeGet('colors.velvet')};
  position: relative;
  height: 70px;
  width: 100%;
  z-index: 999;
`;

const Grid = styled(SectionContainer)`
  z-index: 10;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
`;

const Controls = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const DesktopCtas = styled.div`
  display: none;
  flex: 0 0 auto;
  margin-left: 10px;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    display: flex;
    align-items: center;
  }
`;

const MobileCtas = styled.div`
  display: flex;
  align-items: center;
  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    display: none;
  }
`;

const Logo = styled.img`
  min-width: 127px;
  width: 100%;
  max-width: 127px;
  margin: 0 15px 0 0;

  @media only screen and (min-width: ${themeGet('breakpoints.sm')}) {
    &:lang(fr) {
      margin: 0 15px 0 0;
    }

    margin: 0 30px 0 0;
  }
`;

const HeaderBladeButton = styled(BladeController)`
  ${HeaderButtonStyles}
`;

const MainMenu = styled(BladeController)`
  margin: 0 0 0 10px;

  &:lang(fr) {
    margin: 0 0 0 15px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.sm')}) {
    margin: 0 0 0 20px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    &:lang(fr) {
      margin: 0 0 0 20px;
    }
  }

  @media only screen and (min-width: 1120px) {
    &:lang(en) {
      display: none;
    }
  }

  @media only screen and (min-width: 1440px) {
    &:lang(fr) {
      display: none;
    }
  }
`;

export const query = graphql`
  query {
    allContentfulHeader(
      filter: { internalName: { eq: "Default Site Header" } }
    ) {
      nodes {
        ...HeaderData
      }
    }
    allContentfulNotificationBanner(
      filter: { isShownOnAllPages: { eq: true } }
    ) {
      nodes {
        ...NotificationBanner
      }
    }
  }
`;

interface HeaderProps {
  customHeader?: any;
  customNotifications?: any;
  scrollFocusElement?: any;
  search?: boolean;
}

export const getNotificationBanners = (
  customNotifications = [],
  defaultNotifications = [],
  locale = 'en',
): any[] => {
  const localizedNotifications = defaultNotifications.filter(
    ({ node_locale }: { node_locale: string }) => node_locale.startsWith(locale),
  );
  const uniqueNotifications = customNotifications.filter(
    ({ isShownOnAllPages }: { isShownOnAllPages: boolean }) => !isShownOnAllPages,
  );
  return [...localizedNotifications, ...uniqueNotifications];
};

export const getHeader = (
  customHeader: any,
  defaultHeaders = [],
  locale = 'en',
  activeProvince: string | null = null,
): any => {
  const localizedHeader = defaultHeaders.find(
    ({ node_locale }: { node_locale: string }) => node_locale.startsWith(locale),
  );
  let headerData = customHeader || localizedHeader;
  if (headerData?.renewalHeader) headerData = headerData.renewalHeader;
  return filterHeaderLinksForRegionLocked(
    activeProvince,
    headerData,
  );
};

const Header = ({
  customHeader,
  customNotifications = [],
  scrollFocusElement,
  search = true,
}: HeaderProps) => {
  const intl = useIntl();
  const keyValuePairs = useKeyValuePairs(intl.locale);
  const { activeProvince } = useProvince();
  const { allContentfulHeader, allContentfulNotificationBanner } = useStaticQuery(query);

  const notificationBanners = getNotificationBanners(
    customNotifications,
    allContentfulNotificationBanner.nodes,
    intl.locale,
  );

  const headerData = getHeader(
    customHeader,
    allContentfulHeader.nodes,
    intl.locale,
    activeProvince,
  );

  const headerLinks = headerData?.headerLinks;

  const renderCTAs = (headerCTAs: any) => (
    headerCTAs?.map((cta: any) => {
      if (cta?.internal?.type === 'ContentfulBlade') {
        return <HeaderBladeButton key={cta.id} data={cta} />;
      }
      if (cta?.internal?.type === 'ContentfulButton'
        || cta?.internal?.type === 'ContentfulLinkWrapper') {
        return (
          <GetAQuoteButtonOrLink
            key={cta.id}
            cta={cta}
            CtaComponent={HeaderCallToAction}
          />
        );
      }
      if (cta?.internal?.type === 'ContentfulModal') {
        return (
          <ModalTrigger key={cta.id} data={cta} />
        );
      }
      return null;
    })
  );

  return (
    <Sticky
      enabled
      innerZ={999}
      enableTransforms={false}
      top={0}
    >
      <StyledHeader as="header">
        {notificationBanners.map((notification: any) => (
          <NotificationBar
            key={notification.id}
            notificationData={notification}
          />
        ))}
        <StyledHeaderWrapper>
          <Grid>
            {headerData.clickableLogo === false ? (
              <Logo
                src={headerData.logoDam.asset.localImage.publicURL}
                alt={headerData.logoDam.altText}
              />
            ) : (
              <Link to="/" innerRef={scrollFocusElement}>
                <Logo
                  src={headerData.logoDam.asset.localImage.publicURL}
                  alt={headerData.logoDam.altText}
                />
              </Link>
            )}

            <Controls>
              {headerLinks && (
              <DropdownNavigation navLinks={headerLinks} />
              )}
              {search && (
              <GlobalSearch
                id="global-search"
                type="header"
                placeholder={keyValuePairs.SEARCH_PLACEHOLDER}
                predictiveSearch
              />
              )}
              <DesktopCtas>
                {renderCTAs(headerData?.headerCTAs)}
                <LangAndProvinceSelector />
              </DesktopCtas>
              <MobileCtas>
                {renderCTAs(headerData?.mobileHeaderCTAs)}
              </MobileCtas>
              {headerData.hamburgerMenuBlade && (
              <MainMenu
                data={{
                  bladeType: 'main-menu',
                  ...headerData,
                }}
              />
              )}
            </Controls>
          </Grid>
        </StyledHeaderWrapper>

        {headerData.mobileStickyNav && (
        <MobileStickyNav btnpos={headerData.mobileStickyNavButtonPosition}>
          {headerData.mobileStickyNav.map((cta: any) => {
            if (cta.internal.type === 'ContentfulBlade') {
              return <BladeController key={cta.id} data={cta} />;
            }
            if (cta.internal.type === 'ContentfulButton') {
              return (
                <CallToAction
                  key={cta.id}
                  variant={cta.buttonType}
                  href={cta.buttonUrl}
                >
                  {cta.buttonLabel}
                </CallToAction>
              );
            }
            return null;
          })}
        </MobileStickyNav>
        )}
      </StyledHeader>
    </Sticky>
  );
};

export default Header;
